import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { absoluteUrl, SEO_LOCALE_KEYS, SUPPORTED_SEO_LOCALES, SeoLocaleKey } from './constants';

interface PageSeoProps {
  path: string;
  titleKey: string;
  descriptionKey: string;
  ogImage?: string;
  keywords?: string[];
  noindex?: boolean;
  jsonLd?: Array<Record<string, any>>;
  /*
   * Open Graph type. 'website' suits the marketing pages; an article should say so,
   * which lets a share card show a byline and a date instead of a generic site card.
   * The BlogPosting JSON-LD already carries both, so only the OG side was missing.
   */
  ogType?: 'website' | 'article';
  /** ISO date, emitted as article:published_time. Only meaningful with ogType 'article'. */
  publishedTime?: string;
  /** Emitted as article:author. Only meaningful with ogType 'article'. */
  author?: string;
  /**
   * Whether this route's content exists in more than English.
   *
   * Drives both the canonical and the hreflang set, which previously contradicted
   * each other: every page emitted a complete reciprocal hreflang set naming itself
   * while the canonical pointed at the English URL. The canonical wins, so all 259
   * non-English prerendered pages were told not to be indexed as themselves — the
   * whole locale expansion silently suppressed by its own markup.
   *
   * Flipping the canonical alone would have been worse. The docs sections are
   * hardcoded English JSX and the blog and integration pages come from a single
   * English .mdx each, so 161 of those pages are English text under a foreign URL;
   * making them self-canonical would promote 161 duplicates into the index claiming
   * to be German, Japanese and Korean.
   *
   * So: translated routes get a self-referencing canonical and the full hreflang
   * set. Untranslated ones keep pointing at English and emit no alternates at all,
   * because an alternate that is a duplicate is a worse claim than no alternate.
   * Must stay in step with the `localized` flag in scripts/seo-routes.js, which
   * decides which URLs are prerendered and which get sitemap alternates.
   *
   * Defaults to false so a new page has to opt in deliberately rather than inherit a
   * claim about its own content.
   */
  localized?: boolean;
}

const DEFAULT_OG_IMAGE = '/og/og-default.png';

function localeKeyFromPath(pathname: string): SeoLocaleKey {
  const m = pathname.match(/^\/(es|de|fr|ja|pt-BR|zh-CN|ko)(\/|$)/);
  return m ? (m[1] as SeoLocaleKey) : 'en';
}

export const PageSeo: React.FC<PageSeoProps> = ({
  path, titleKey, descriptionKey, ogImage, keywords, noindex, jsonLd,
  ogType = 'website', publishedTime, author, localized = false,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const localeKey = localeKeyFromPath(location.pathname);
  const ogLocale = SUPPORTED_SEO_LOCALES[localeKey].ogLocale;
  const fullTitle = `${t(titleKey)} — Localingos`;
  const description = t(descriptionKey);
  // Self-referencing where the content is genuinely translated; English otherwise.
  const canonical = localized ? absoluteUrl(path, localeKey) : absoluteUrl(path);
  const pageUrl = absoluteUrl(path, localeKey);
  const imageUrl = absoluteUrl(ogImage || DEFAULT_OG_IMAGE);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && keywords.length > 0 && <meta name="keywords" content={keywords.join(', ')} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <link rel="canonical" href={canonical} />

      {localized && SEO_LOCALE_KEYS.map(k => (
        <link key={k} rel="alternate" hrefLang={SUPPORTED_SEO_LOCALES[k].hreflang} href={absoluteUrl(path, k)} />
      ))}
      {localized && <link rel="alternate" hrefLang="x-default" href={absoluteUrl(path)} />}

      <meta property="og:type" content={ogType} />
      {ogType === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {ogType === 'article' && author && <meta property="article:author" content={author} />}
      <meta property="og:site_name" content="Localingos" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:locale" content={ogLocale} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {jsonLd?.map((schema, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(schema)}</script>
      ))}
    </Helmet>
  );
};
